/**
 * Parse island props from a host element's data attributes.
 *
 * Reads individual `data-props-*` attributes (the browser auto-converts these
 * to camelCase in `element.dataset`, e.g. `data-props-product-id` becomes the
 * key `propsProductId`) and merges them with any `data-props` JSON object.
 * JSON wins per key, so typed values (numbers, booleans, arrays) set via JSON
 * always override string attributes with the same name.
 *
 * @example
 * // <div data-island="badge" data-props-product-id="123" />
 * // → { productId: "123" }
 *
 * // <div data-island="plp" data-props-account-id="abc" data-props='{"rows":20}' />
 * // → { accountId: "abc", rows: 20 }
 *
 * // Both set productId — JSON wins:
 * // <div data-props-product-id="old" data-props='{"productId":"new"}' />
 * // → { productId: "new" }
 */
export function parseIslandProps(element: HTMLElement): Record<string, unknown> {
  const attrProps: Record<string, unknown> = {};

  for (const [dataKey, value] of Object.entries(element.dataset)) {
    // Accept keys like "propsProductId" but not "props" itself (length > 5)
    if (dataKey.startsWith("props") && dataKey.length > 5) {
      // "propsProductId" → strip "props" (5 chars), lowercase first remaining char
      const propName = dataKey.charAt(5).toLowerCase() + dataKey.slice(6);
      attrProps[propName] = value;
    }
  }

  const propsJson = element.getAttribute("data-props");
  if (!propsJson) return attrProps;

  try {
    const jsonProps = JSON.parse(propsJson) as Record<string, unknown>;
    return { ...attrProps, ...jsonProps }; // JSON wins per key
  } catch (e) {
    console.warn("[Aurora] Failed to parse data-props JSON — falling back to attribute props:", e);
    return attrProps;
  }
}
